import { markerSpread } from './spread.js';
import { addDays } from './period.js';
export function navIndex(list, id) {
    const i = list.findIndex((c) => c.campaignId === id);
    if (i < 0)
        return { position: null, total: list.length, prev: null, next: null };
    return {
        position: i + 1,
        total: list.length,
        prev: i > 0 ? (list[i - 1]?.campaignId ?? null) : null,
        next: i < list.length - 1 ? (list[i + 1]?.campaignId ?? null) : null,
    };
}
/** Все дни окна по порядку; дней без данных в ответе нет — дополняем нулями. */
export function fillDays(from, to, list) {
    const byDay = new Map(list.map((d) => [d.day, d]));
    const out = [];
    for (let d = from, i = 0; d <= to && i < 400; d = addDays(d, 1), i++) {
        out.push(byDay.get(d) ?? { day: d, spendMinor: 0, purchases: 0, cppMinor: null });
    }
    return out;
}
/**
 * Цена покупки скользящим окном 7 дней: Σрасход₇ / Σпокупки₇ (центы). Без
 * покупок в окне — `null`.
 */
export function rolling7Cpp(list) {
    return list.map((d, i) => {
        let spend = 0;
        let n = 0;
        for (let j = Math.max(0, i - 6); j <= i; j++) {
            spend += list[j]?.spendMinor ?? 0;
            n += list[j]?.purchases ?? 0;
        }
        return { day: d.day, cpp: n > 0 ? spend / n : null };
    });
}
/** Последнее известное значение ряда. */
export function lastValue(series) {
    for (let i = series.length - 1; i >= 0; i--) {
        const v = series[i]?.cpp;
        if (v !== null && v !== undefined)
            return v;
    }
    return null;
}
/**
 * Признаки, сильнее всего разделяющие покупателей кампании: разброс по
 * значениям от `minRank` покупок, показываются значения от `minShow`.
 * Признаки без разброса (одно крупное значение) не попадают.
 */
export function topDraggingMarkers(markers, minRank = 10, minShow = 5) {
    const out = [];
    Object.entries(markers).forEach(([key, m], i) => {
        if (!m.available)
            return;
        const spread = markerSpread(m.values, minRank);
        if (!spread || spread.width <= 0)
            return;
        const total = Object.values(m.values).reduce((s, g) => s + g.purchases, 0);
        const values = Object.entries(m.values)
            .filter(([, g]) => g.purchases >= minShow)
            .map(([value, g]) => ({
            value,
            n: g.purchases,
            roas: g.answer.roas3,
            share: total > 0 ? g.purchases / total : 0,
            faint: g.purchases < minRank,
        }))
            .sort((a, b) => (a.roas ?? Infinity) - (b.roas ?? Infinity));
        out.push({ key, spread, values, i });
    });
    out.sort((a, b) => b.spread.width - a.spread.width || a.i - b.i);
    return out.map(({ key, spread, values }) => ({ key, spread, values }));
}
/**
 * Сравнение самого слабого значения (от `minRank` покупок) первого признака
 * с тем же значением по всему портфелю (`R.markers`).
 */
export function compareWithPortfolio(top, portfolioMarkers) {
    if (!top)
        return null;
    const weakest = top.values.find((v) => !v.faint && v.roas !== null);
    if (!weakest)
        return null;
    const pm = portfolioMarkers[top.key];
    let portfolioRoas = null;
    let portfolioShare = null;
    if (pm?.available) {
        const g = pm.values[weakest.value];
        const total = Object.values(pm.values).reduce((s, x) => s + x.purchases, 0);
        portfolioRoas = g?.answer.roas3 ?? null;
        portfolioShare = g && total > 0 ? g.purchases / total : null;
    }
    return {
        marker: top.key,
        value: weakest.value,
        portfolioRoas,
        campaignShare: weakest.share,
        portfolioShare,
        overRepresented: portfolioShare !== null && weakest.share > portfolioShare * 1.25,
    };
}
// --- ссылки -----------------------------------------------------------------
/** Ads Manager на этой кампании; без кабинета — `null`. */
export function adsManagerUrl(adAccountId, campaignId) {
    const act = (adAccountId ?? '').replace(/^act_/, '').trim();
    if (!/^\d+$/.test(act) || !/^\d+$/.test(campaignId))
        return null;
    const q = new URLSearchParams({ act, selected_campaign_ids: campaignId });
    return `https://adsmanager.facebook.com/adsmanager/manage/campaigns?${q.toString()}`;
}
const BUCKETS = [
    { key: 'past_due', label: 'оплата не прошла', hint: 'Stripe повторяет' },
    { key: 'trialing', label: 'триал' },
    { key: 'canceled', label: 'отменено' },
    { key: 'active', label: 'активны' },
    { key: 'other', label: 'другое' },
];
/** Статусы подписок сейчас — в пять понятных групп, пустые не показываются. */
export function statusBuckets(statusNow) {
    const counts = {
        past_due: 0,
        trialing: 0,
        canceled: 0,
        active: 0,
        other: 0,
    };
    for (const [k, n] of Object.entries(statusNow)) {
        const key = (['past_due', 'trialing', 'canceled', 'active'].includes(k) ? k : 'other');
        counts[key] += n;
    }
    const total = Object.values(counts).reduce((s, n) => s + n, 0);
    return BUCKETS.filter((b) => counts[b.key] > 0).map((b) => ({
        ...b,
        count: counts[b.key],
        share: total > 0 ? counts[b.key] / total : 0,
    }));
}
/**
 * Шкала цены покупки: от min до max из цены, «в ноль» и «уверенно» ±20 %,
 * округлённые до $5.
 */
export function priceScale(values) {
    const xs = values.filter((v) => typeof v === 'number' && Number.isFinite(v));
    if (xs.length === 0)
        return null;
    const step = 500;
    const min = Math.max(0, Math.floor((Math.min(...xs) * 0.8) / step) * step);
    const max = Math.ceil((Math.max(...xs) * 1.2) / step) * step;
    const span = max - min || 1;
    return {
        min,
        max,
        pos: (v) => Math.round(Math.min(100, Math.max(0, ((v - min) / span) * 100)) * 100) / 100,
    };
}
/** Насколько `target` ниже/выше `base`: «−41 %». */
export function relPct(target, base) {
    if (target === null || base === null || base === 0)
        return null;
    const d = Math.round(((target - base) / base) * 100);
    return `${d < 0 ? '−' : '+'}${Math.abs(d)}\u202F%`;
}
// --- моменты ------------------------------------------------------------------
export const MOMENT_LABEL = {
    t0: 'Сразу при покупке',
    t24: 'Через сутки',
    d14: 'На 14-й день',
};
//# sourceMappingURL=campaign-sheet-logic.js.map